import { yupResolver } from '@hookform/resolvers/yup';
import { Stack, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { useOrganicRegistrationForm } from '..';
import {
  handleDirectAccessLogin,
  useLoginMutation,
} from '../../../../../api/registration/login';
import { useRegisterOtvcUserMutation } from '../../../../../api/registration/otvc/registerUser';
import Verification from '../../../../../assets/icons/Verification';
import Button from '../../../../../components/styledComponents/Button';
import { LinkWithNoRouter } from '../../../../../components/styledComponents/Link';
import colors from '../../../../../constants/colors';
import { useDirectAccessAnalytics } from '../../../../../hooks/useAnalytics';
import { useSnackbarsContext } from '../../../../../store/SnackbarProvider';
import DateOfBirthTextField from '../../../Components/DateOfBirthTextField';
import SplitView from '../../../SplitView';
import {
  OneTimeVerificationRegister,
  registerOneTimeVerificationCodeSchema,
} from '../../schemas/oneTimeVerificationCode';

const Register = () => {
  const { formValues, otvcValues } = useOrganicRegistrationForm();
  const registerOtvcUserMutation = useRegisterOtvcUserMutation();
  const loginMutation = useLoginMutation();
  const { track } = useDirectAccessAnalytics();
  const { closeSnackbar, enqueueSnackbar } = useSnackbarsContext();

  useEffect(() => {
    if (!otvcValues?.codeSent) {
      navigate('/register', { replace: true });
    }
  }, []);

  const showGenericErrorBanner = () =>
    enqueueSnackbar({
      key: 'policy-search-organic-registration-error',
      variant: 'error',
      title: 'We are unable to process your request right now.',
      content: (
        <>
          Please check the details and try again, or contact Service at {}
          <LinkWithNoRouter
            href="tel:+888.980.5650"
            underline="none"
            sx={{
              fontSize: {
                mobile: 16,
                tablet: 18,
              },
              fontWeight: 400,
              fontFamily: 'Cabin',
              fontKerning: 'normal',
              color: colors.csaaSpectrumBlue50,
            }}
          >
            888.980.5650
          </LinkWithNoRouter>
          .
        </>
      ),
    });

  const navigate = useNavigate();
  const {
    control,
    formState: { isValid },
    handleSubmit,
  } = useForm<OneTimeVerificationRegister>({
    resolver: yupResolver(registerOneTimeVerificationCodeSchema), // placeholder phone number that will come from API
    mode: 'onBlur',
  });

  const handleLogin = async (email: string, password: string) => {
    try {
      const res = await loginMutation.mutateAsync({ email, password });
      if (res.sessionToken) {
        track('Registration Completed', {
          event_type: 'Automated System Process',
          location: 'Feature Card',
          registration_type: 'OTVC',
          flow: otvcValues.flow,
          email: formValues.email,
          login_method: 'DA',
        });
        handleDirectAccessLogin(res.sessionToken);
      } else {
        throw new Error('No session token returned from Login service');
      }
    } catch (err) {
      track('Registration Failed', {
        event_type: 'Automated System Process',
        location: 'Feature Card',
        registration_type: 'OTVC',
        flow: otvcValues.flow,
        reason:
          (err as Error)?.message ||
          'No session token returned from Login service',
        login_method: 'DA',
      });
      navigate('/login');
    }
  };

  const register = async (dateOfBirth: string) => {
    const { email, password } = formValues;
    closeSnackbar();
    try {
      await registerOtvcUserMutation.mutateAsync({
        emailId: email,
        password,
        dob: dateOfBirth,
      });
      await handleLogin(email, password);
    } catch (err) {
      showGenericErrorBanner();
    }
  };

  const disableContinue =
    !isValid || registerOtvcUserMutation.isPending || loginMutation.isPending;

  const onSubmit = async ({ dateOfBirth }: OneTimeVerificationRegister) => {
    await register(dateOfBirth);
  };

  return (
    <SplitView data-testid="organic-registration-1" Icon={<Verification />}>
      <Stack
        justifyContent={{ mobile: 'flex-start', desktop: 'center' }}
        direction="column"
        sx={{ px: { mobile: 2, tablet: 14, desktop: 11 }, height: '100%' }}
        component="form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Typography
          variant="h1"
          sx={{
            fontWeight: 700,
            fontSize: { mobile: 33, tablet: 44 },
            lineHeight: { mobile: '48px', desktop: '56px' },
          }}
        >
          Verify your identity
        </Typography>
        <Typography
          variant="h2"
          sx={{
            fontWeight: 500,
            fontSize: { mobile: 23, tablet: 28 },
            lineHeight: { mobile: '32px', desktop: '40px' },
            mt: 1,
            color: colors.csaaDark,
          }}
        >
          For your security, answer the following question.
        </Typography>
        <Stack direction="column" spacing={1} sx={{ mt: 3 }}>
          <Typography
            variant="h2"
            sx={{
              fontFamily: 'Cabin',
              fontWeight: 400,
              fontSize: { mobile: 14, tablet: 16 },
              lineHeight: '24px',
              color: colors.csaaDark,
            }}
          >
            By continuing, you accept our{' '}
            <LinkWithNoRouter
              href="https://csaa-insurance.aaa.com/content/aaa-ie/b2c/en/misc/terms-of-use.html"
              target="_blank"
              onClick={() =>
                track('Terms and Conditions Accessed', {
                  event_type: 'Link Accessed',
                  link: 'Terms of Use',
                  location: 'Footer',
                })
              }
              sx={{
                fontSize: 'inherit',
                fontWeight: 'inherit',
                fontFamily: 'inherit',
              }}
            >
              Terms of Use
            </LinkWithNoRouter>
            .
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontFamily: 'Cabin',
              fontWeight: 400,
              fontSize: { mobile: 14, tablet: 16 },
              lineHeight: '24px',
              color: colors.csaaDark,
            }}
          >
            What is the named insured's date of birth?
          </Typography>
        </Stack>
        <DateOfBirthTextField
          name="dateOfBirth"
          control={control}
          className="csaa-qm-block"
          label="Date of birth"
          sx={{ mt: 3 }}
          preventWhitespace
          inputProps={{ maxLength: 10, 'data-testid': 'otvc-dob-field' }}
          showVisibilityToggle
        />
        <Stack
          direction="row"
          justifyContent="flex-end"
          sx={{ mt: { mobile: 3, tablet: 5 } }}
        >
          <Button
            type="submit"
            onClick={() => {
              window.scrollTo(0, 0);
            }}
            data-testid="otvc-continue-button"
            disabled={disableContinue}
            loading={
              registerOtvcUserMutation.isPending || loginMutation.isPending
            }
          >
            Continue
          </Button>
        </Stack>
      </Stack>
    </SplitView>
  );
};

export default Register;
